import React, { useState, useEffect, useRef, useCallback, useLayoutEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import oracleDataManager from '../../services/oracleDataManager';
import oracleSessionService from '../../services/oracleSessionService';
import ErrorHandler from '../../services/errorHandler';
import { ORACLE_STEPS, ANIMATIONS } from '../../constants/oracleConstants';
import RaySelection from './steps/RaySelection';
import QuestionInput from './steps/QuestionInput';
import CardSelection from './steps/CardSelection';
import OracleOutput from './steps/OracleOutput';
import MasterReview from './steps/MasterReview';
import MagicalBackground from '../MagicalBackground/MagicalBackground';
import FeedbackButton from '../FeedbackButton/FeedbackButton';
import FeedbackModal from '../FeedbackModal/FeedbackModal';
import './OracleFlow.css';

const OracleFlow = () => {
  const { sessionId } = useParams();
  const { user, isVerified } = useAuth();
  const navigate = useNavigate();
  const timeoutsRef = useRef(new Set());

  const [sessionData, setSessionData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [currentStep, setCurrentStep] = useState(ORACLE_STEPS.FIRST_STEP); // Start from ray selection
  const [isTransitioning, setIsTransitioning] = useState(false); // Track step transition state
  const [usageIncremented, setUsageIncremented] = useState(false); // Track if we've already incremented usage for this session
  const [showFinalLoading, setShowFinalLoading] = useState(false); // TD3-120: Loading state for MasterReview transition
  const [showFeedbackModal, setShowFeedbackModal] = useState(false); // TD3-105: Feedback modal state

  // TD3-201: Force scroll to top when OracleFlow loads (same fix as Dashboard TD3-200)
  useLayoutEffect(() => {
    console.log('🔮 OracleFlow useLayoutEffect: Forcing scroll to top');
    // Disable smooth scroll temporarily
    const html = document.documentElement;
    const body = document.body;
    html.style.scrollBehavior = 'auto';
    body.style.scrollBehavior = 'auto';

    // Force scroll three ways (cross-browser compatibility)
    window.scrollTo(0, 0);
    html.scrollTop = 0;
    body.scrollTop = 0;

    console.log(`📏 OracleFlow forced scroll - window.scrollY: ${window.scrollY}`);

    // Restore smooth scroll after render
    setTimeout(() => {
      html.style.scrollBehavior = '';
      body.style.scrollBehavior = '';
    }, 100);
  }, []); // Run once on mount

  const loadOracleSession = useCallback(async () => {
    if (!user || !sessionId) return;

    try {
      setLoading(true);

      // TD3-127: Try loading from PocketBase first (for viewing past oracles)
      console.log(`🔍 Loading oracle session: ${sessionId}`);
      const pbResult = await oracleSessionService.getSession(sessionId);

      let session = null;

      if (pbResult.success) {
        // Found in PocketBase - extract sessionData
        session = pbResult.session.sessionData;
        console.log(`✅ Loaded session from PocketBase: ${session.sessionId}`);
      } else {
        // Not in PocketBase, try localStorage (for new/in-progress sessions)
        console.log(`⚠️ Not found in PocketBase, checking localStorage...`);
        session = await oracleDataManager.getOracleSession(sessionId, user.id);

        if (session) {
          console.log(`✅ Loaded session from localStorage: ${session.sessionId}`);
        }
      }

      if (!session) {
        const errorMsg = 'Sessão do oráculo não encontrada';
        setError(errorMsg);
        await ErrorHandler.captureError(
          ErrorHandler.ERROR_TYPES.NAVIGATION,
          `Oracle session not found in PocketBase or localStorage: ${sessionId}`,
          user.id,
          { sessionId, attemptedUserId: user.id }
        );
        return;
      }

      setSessionData(session);
      // Ensure we start at minimum step (oracle flow starts here)
      const step = Math.max(session.currentStep || ORACLE_STEPS.FIRST_STEP, ORACLE_STEPS.FIRST_STEP);
      setCurrentStep(step);
    } catch (err) {
      const errorMsg = 'Erro ao carregar sessão do oráculo';
      setError(errorMsg);
      await ErrorHandler.captureError(
        ErrorHandler.ERROR_TYPES.DATABASE,
        `Failed to load oracle session: ${err.message}`,
        user.id,
        { sessionId, errorStack: err.stack }
      );
    } finally {
      setLoading(false);
    }
  }, [sessionId, user]);

  useEffect(() => {
    if (!user) {
      navigate('/auth');
      return;
    }

    // TD3-123: Check email verification before allowing Oracle access
    if (!isVerified) {
      navigate('/dashboard'); // Redirect to dashboard, which will show VerificationRequired
      return;
    }

    loadOracleSession();
  }, [user, isVerified, navigate, loadOracleSession]);

  // Cleanup timeouts on unmount
  useEffect(() => {
    return () => {
      const timeouts = timeoutsRef.current;
      timeouts.forEach(timeoutId => clearTimeout(timeoutId));
      timeouts.clear();
    };
  }, []);

  const updateSession = useCallback(async (updates) => {
    try {
      const updatedSession = await oracleDataManager.updateOracleSession(sessionId, updates);
      setSessionData(updatedSession);
      return updatedSession;
    } catch (err) {
      await ErrorHandler.captureError(
        ErrorHandler.ERROR_TYPES.DATABASE,
        `Failed to update oracle session: ${err.message}`,
        user?.id,
        { sessionId, updates, errorStack: err.stack }
      );
      throw err;
    }
  }, [sessionId, user?.id]);

  const handleStepComplete = async (stepData) => {
    if (isTransitioning) return; // Prevent multiple transitions
    
    try {
      // Start fade out transition
      setIsTransitioning(true);
      
      // Check if we're entering UI step 7 (internal step 12) and increment usage
      const nextStep = currentStep + 1;
      if (nextStep === 12 && !usageIncremented && user) {
        try {
          // Pass oracle session ID for complete audit trail
          const result = await oracleSessionService.incrementOracleUsage(user.id, sessionId);
          if (result.success) {
            setUsageIncremented(true);
          } else {
            await ErrorHandler.captureError(
              ErrorHandler.ERROR_TYPES.DATABASE,
              `Failed to increment oracle usage: ${result.error}`,
              user.id,
              { sessionId, step: nextStep }
            );
          }
        } catch (error) {
          await ErrorHandler.captureError(
            ErrorHandler.ERROR_TYPES.DATABASE,
            `Error incrementing oracle usage: ${error.message}`,
            user.id,
            { sessionId, step: nextStep, errorStack: error.stack }
          );
        }
      }
      
      // Wait for fade out to complete, then update step
      const fadeOutTimeout = setTimeout(async () => {
        try {
          const updates = { ...stepData, currentStep: nextStep };
          await updateSession(updates);
          setCurrentStep(nextStep);

          // TD3-120: Add 2-3 second loading state before showing MasterReview
          if (nextStep === 16) {
            setShowFinalLoading(true);
            const finalLoadingTimeout = setTimeout(() => {
              setShowFinalLoading(false);
              timeoutsRef.current.delete(finalLoadingTimeout);
            }, 2500); // 2.5 seconds
            timeoutsRef.current.add(finalLoadingTimeout);
          }

          // Scroll to top immediately and ensure it happens
          setTimeout(() => {
            window.scrollTo(0, 0);
            document.documentElement.scrollTop = 0;
            document.body.scrollTop = 0;
          }, 0);

          // Start fade in
          const fadeInTimeout = setTimeout(() => {
            setIsTransitioning(false);
            timeoutsRef.current.delete(fadeInTimeout);
          }, 50); // Small delay to ensure DOM update
          timeoutsRef.current.add(fadeInTimeout);
          
        } catch (err) {
          await ErrorHandler.captureError(
            ErrorHandler.ERROR_TYPES.NAVIGATION,
            `Error completing step ${nextStep}: ${err.message}`,
            user?.id,
            { sessionId, currentStep, nextStep, stepData, errorStack: err.stack }
          );
          setError('Erro ao salvar progresso');
          setIsTransitioning(false);
        }
        
        timeoutsRef.current.delete(fadeOutTimeout);
      }, ANIMATIONS.STEP_TRANSITION_DURATION);
      
      timeoutsRef.current.add(fadeOutTimeout);
      
    } catch (err) {
      await ErrorHandler.captureError(
        ErrorHandler.ERROR_TYPES.NAVIGATION,
        `Error starting step transition: ${err.message}`,
        user?.id,
        { sessionId, currentStep, stepData, errorStack: err.stack }
      );
      setError('Erro ao salvar progresso');
      setIsTransitioning(false);
    }
  };

  const handleNavigation = (step) => {
    // Allow navigation to completed steps or current step
    if (step <= currentStep && step !== currentStep && !isTransitioning) {
      // Start fade transition for navigation
      setIsTransitioning(true);
      
      const fadeOutTimeout = setTimeout(() => {
        setCurrentStep(step);
        // Force scroll to top
        window.scrollTo(0, 0);
        document.documentElement.scrollTop = 0;
        document.body.scrollTop = 0;
        
        const fadeInTimeout = setTimeout(() => {
          setIsTransitioning(false);
          timeoutsRef.current.delete(fadeInTimeout);
        }, 50);
        timeoutsRef.current.add(fadeInTimeout);
        
        timeoutsRef.current.delete(fadeOutTimeout);
      }, ANIMATIONS.STEP_TRANSITION_DURATION);
      
      timeoutsRef.current.add(fadeOutTimeout);
    }
  };

  const handleNavigateBack = () => {
    if (isTransitioning) return; // Prevent multiple transitions

    // TD3-120: Scroll to top before navigation
    window.scrollTo(0, 0);
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;

    // Start fade out transition
    setIsTransitioning(true);

    const fadeOutTimeout = setTimeout(() => {
      // Navigate to dashboard after fade out
      navigate('/dashboard');
      timeoutsRef.current.delete(fadeOutTimeout);
    }, ANIMATIONS.STEP_TRANSITION_DURATION);

    timeoutsRef.current.add(fadeOutTimeout);
  };

  if (!user) {
    return null; // Will redirect in useEffect
  }

  if (loading) {
    return (
      <div className="oracle-flow-container">
        <div className="loading-state">
          <div className="loading-spinner"></div>
          <p>Carregando oráculo...</p>
        </div>
      </div>
    );
  }

  if (error || !sessionData) {
    return (
      <div className="oracle-flow-container">
        <div className="error-state">
          <h2>Erro</h2>
          <p>{error || 'Sessão não encontrada'}</p>
          <button onClick={handleNavigateBack} className="back-btn primary-cta">
            Voltar ao Dashboard
          </button>
        </div>
      </div>
    );
  }

  const renderCurrentStep = () => {
    switch (currentStep) {
      case 6:
        return (
          <RaySelection
            sessionData={sessionData}
            onComplete={(raySelection) => handleStepComplete({ raySelection })}
          />
        );
      
      case 7:
        return (
          <QuestionInput
            sessionData={sessionData}
            onComplete={(userQuestion) => handleStepComplete({ userQuestion })}
          />
        );
      
      case 8:
        return (
          <CardSelection
            sessionData={sessionData}
            selectionType="diagnosis"
            title="Escolha uma carta para o diagnóstico"
            cardCount={9}
            onComplete={(selection) => handleStepComplete({ diagnosisType: selection })}
          />
        );
      
      case 9:
        return (
          <CardSelection
            sessionData={sessionData}
            selectionType="diagnosisElement"
            title="Escolha o elemento do diagnóstico"
            cardCount={4}
            onComplete={(selection) => handleStepComplete({ diagnosisElement: selection })}
          />
        );
      
      case 10:
        return (
          <CardSelection
            sessionData={sessionData}
            selectionType="solution"
            title="Escolha uma carta para a solução"
            cardCount={9}
            onComplete={(selection) => handleStepComplete({ solutionType: selection })}
          />
        );
      
      case 11:
        return (
          <>
            {/* TD3-202: Subtle notice that next step will count usage */}
            <div className="oracle-policy-notice" style={{ marginBottom: '1.5rem' }}>
              <span className="notice-icon">ℹ️</span>
              <p>
                Ao clicar em 'Próximo', sua leitura personalizada será gerada.
                Isso contará como 1 das suas 3 consultas mensais.
              </p>
            </div>
            <CardSelection
              sessionData={sessionData}
              selectionType="solutionElement"
              title="Escolha o elemento da solução"
              cardCount={4}
              onComplete={(selection) => handleStepComplete({ solutionElement: selection })}
            />
          </>
        );
      
      case 12:
        return (
          <OracleOutput
            key="blockage-12"
            sessionData={sessionData}
            outputType="blockage"
            title="Seu Bloqueio"
            onComplete={() => handleStepComplete({})}
            updateSession={updateSession}
          />
        );
      
      case 13:
        return (
          <OracleOutput
            key="solution-13"
            sessionData={sessionData}
            outputType="solution"
            title="Para Desbloquear"
            onComplete={() => handleStepComplete({})}
            updateSession={updateSession}
          />
        );
      
      case 14:
        return (
          <OracleOutput
            key="wisdom-14"
            sessionData={sessionData}
            outputType="wisdom"
            title="Sabedoria Ancestral"
            onComplete={() => handleStepComplete({})}
            updateSession={updateSession}
          />
        );
      
      case 15:
        return (
          <OracleOutput
            key="decree-15"
            sessionData={sessionData}
            outputType="decree"
            title="Seu Decreto Pessoal"
            onComplete={() => handleStepComplete({ isComplete: true, completedAt: new Date().toISOString() })}
            updateSession={updateSession}
          />
        );
      
      case 16:
        // TD3-120: Show loading screen for 2-3 seconds before MasterReview
        if (showFinalLoading) {
          return (
            <div className="loading-state">
              <div className="loading-spinner"></div>
              <p>Preparando sua consulta completa...</p>
            </div>
          );
        }

        return (
          <MasterReview
            sessionData={sessionData}
            onBackToDashboard={handleNavigateBack}
            onNavigation={handleNavigation}
          />
        );
      
      default:
        // If step is 1-5, redirect to step 6 (start of oracle flow)
        if (currentStep < 6) {
          // Don't loop - this should be handled by session initialization
          return (
            <div className="loading-state">
              <div className="loading-spinner"></div>
              <p>Inicializando oráculo...</p>
            </div>
          );
        }
        
        return (
          <div className="error-state">
            <h2>Etapa não encontrada</h2>
            <p>Etapa {currentStep} não implementada</p>
            <button onClick={handleNavigateBack} className="back-btn primary-cta">
              Voltar ao Dashboard
            </button>
          </div>
        );
    }
  };

  return (
    <div className="oracle-flow-container">
      {/* Background animation - Theme-dependent MagicalBackground for all oracle steps */}
      {/* 
        FUTURE THEME EXPANSION NOTES:
        - Current: 'basic' theme (unisex+none) - purple/blue mystical particles
        - Future themes could have completely different:
          * Color schemes (warm/cool palettes)
          * Particle types (hearts, coins, spiritual symbols)
          * Animation styles (gentle/energetic movements)
          * Background patterns (geometric, organic, celestial)
          * Intensity levels based on theme personality
        - Theme possibilities from oracleDataManager:
          * masculine-heart, masculine-wealth, masculine-purpose, masculine-neutral
          * feminine-heart, feminine-wealth, feminine-purpose, feminine-neutral  
          * universal-heart, universal-wealth, universal-purpose, basic
      */}
      <MagicalBackground 
        intensity="medium" 
        theme={sessionData?.themeId || 'basic'}
      />
      
      {/* Mobile warning */}
      <div className="oracle-mobile-warning">
        <h2>Oráculo dos 12 Raios</h2>
        <p>
          Esta aplicação foi otimizada para laptops e desktops com resolução mínima de 1200px.
          Para uma melhor experiência, acesse em um dispositivo maior.
        </p>
      </div>

      {/* Main flow for laptop screens */}
      <div className="oracle-flow-main">
        <div className="flow-header">
          <div className="progress-container">
            <div className="progress-bar">
              <div 
                className="progress-fill"
                style={{ width: `${(Math.max(currentStep - 5, 1) / 11) * 100}%` }}
              ></div>
            </div>
            <div className="progress-text">
              <span>Etapa {Math.max(currentStep - 5, 1)} de 11</span>
            </div>
          </div>
          <div className="header-actions">
            {currentStep === 16 && (
              <div className="fullscreen-tip">
                <span className="tip-icon">🖥️</span>
                <div className="tip-content">
                  <small>Melhor leitura:</small>
                  <div className="shortcuts">
                    <span><strong>F11</strong> (Windows)</span>
                    <span><strong>⌘⇧F</strong> (Mac)</span>
                  </div>
                  <small className="toggle-note">Pressione novamente para sair</small>
                </div>
              </div>
            )}
            <button
              onClick={handleNavigateBack}
              className="exit-btn secondary-cta"
            >
              Voltar
            </button>
          </div>
        </div>

        <div className={`flow-content ${currentStep === 16 || currentStep === 6 || (currentStep >= 7 && currentStep <= 11) ? 'master-review-content' : ''} ${isTransitioning ? 'transitioning' : ''}`}>
          {renderCurrentStep()}
        </div>
      </div>

      {/* TD3-105: Feedback System */}
      <FeedbackButton onClick={() => setShowFeedbackModal(true)} />
      <FeedbackModal
        isOpen={showFeedbackModal}
        onClose={() => setShowFeedbackModal(false)}
        currentPage={`oracle-step-${currentStep}`}
      />
    </div>
  );
};

export default OracleFlow;